import { Box, Button, Text, Tooltip } from "@powerledger/ui-component-lib";
import currency from "currency.js";
import { format } from "date-fns";
import { TFunction } from "i18next";
import { Column, Row } from "react-table";
import ReactTooltip from "react-tooltip";

import { ForwardTradeAttributesTooltip } from "@/app/components/forward-trade-attributes-tooltip";
import { TableValueWrapper } from "@/app/components/table-value-wrapper";
import { HeaderWithToolTip } from "@/app/components/tooltips";
import { AppUserAccountInfo } from "@/app/context";
import { useActiveRegistryDisplayName } from "@/app/hooks/use-active-registry-display-name";
import { canAcceptForwardOrder } from "@/app/lib/can-accept-forward-order";
import { AppDateFormats, formatDate } from "@/app/lib/format-date";
import { AccountType, RecAttributes, TradePosition } from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { ForwardMarketTradeContract } from "../components";

export const getForwardTradeMarketColumns = (
  t: TFunction,
  accountData: {
    accountType?: AccountType | null;
    id?: string;
    linkedRegistries: AppUserAccountInfo["linkedRegistries"];
  },
  onAcceptDeal: (tradeContract: ForwardMarketTradeContract) => void,
  enableCrossRegistryForwardTrading: boolean,
) => {
  const tableColumns: Column<ForwardMarketTradeContract>[] = [
    {
      id: "createdDate",
      Header: <HeaderWithToolTip titleText="OLD" tooltipText="Order Lodgement Date" />,
      accessor: "createdDate",
      Cell: ({ value }) => {
        const displayValue = format(new Date(value), AppDateFormats.AbbreviatedMonthFormat);
        return (
          <TableValueWrapper value={displayValue} csv title="Order Lodgement Date" containerSx={{ minWidth: 80 }} />
        );
      },
    },
    {
      Header: <HeaderWithToolTip titleText="TSD" tooltipText="Trade Settlement Date" />,
      accessor: "tradeSettlementDate",
      Cell: ({ value }) => {
        const displayValue = formatDate(value, {
          formatStyle: AppDateFormats.AbbreviatedMonthFormat,
          noTZ: true,
          noOffset: true,
        });
        return (
          <TableValueWrapper value={displayValue} csv title="Trade Settlement Date" containerSx={{ minWidth: 80 }} />
        );
      },
    },
    {
      Header: (
        <Box
          sx={{
            textAlign: "right",
            width: "100px",
          }}
        >
          {t("Price USD")}
        </Box>
      ),
      width: "100px",
      accessor: "unitPrice",
      Cell: ({ value }) => {
        return (
          <Box
            sx={{
              textAlign: "right",
              width: "120px",
              color: "textDark",
              px: 2,
            }}
          >
            {currency(value || 0, { separator: ",", fromCents: true }).format()}
          </Box>
        );
      },
    },
    {
      Header: () => t("Quantity"),
      accessor: "volume",
      Cell: ({ value }) => <TableValueWrapper value={value} />,
    },
    ...(enableCrossRegistryForwardTrading
      ? ([
          {
            Header: () => t("Registry"),
            accessor: "registryCode",
            disableSortBy: true,
            Cell: ({ value }) => (
              <TableValueWrapper
                value={
                  value
                    ? useActiveRegistryDisplayName(value as TraceXSupportedRegistries)?.activeRegistryDisplayName
                    : "Any"
                }
              />
            ),
          },
        ] as Column<ForwardMarketTradeContract>[])
      : []),
    {
      Header: t("Product Attributes"),
      accessor: "attributes",
      Cell: ({ row }) => {
        const { formattedAttributes, attributes, registryCode } = row.original;

        // Filter out formatted attributes with content that include 'Any' or '-' i.e. that are null in original attributes to display in the table row
        const displayAttributes = Object.fromEntries(
          Object.entries(formattedAttributes).filter(([key, _]) => !!attributes[key as keyof RecAttributes]),
        );

        const { vintages, ...rest } = displayAttributes;
        delete rest.__typename;

        const tooltipRowData = {
          attributes: {
            commencementDate: attributes.commencementDate,
            fuelSources: attributes.fuelSources,
          },
          vintages: formattedAttributes.vintages,
          certificationsAndEligibilities: formattedAttributes.certificationsAndEligibilities,
          fuelSources: formattedAttributes.fuelSources,
          certificateTypes: formattedAttributes.certificateTypes,
        };

        const tooltipContent = (dataTip: string) => {
          return dataTip === "rec-tooltip" ? (
            <Box sx={{ color: "text", whiteSpace: "pre-line", backgroundColor: "shadow", p: 3, borderRadius: 8 }}>
              <ForwardTradeAttributesTooltip
                tooltipData={tooltipRowData}
                rowRegistryCode={registryCode as AggregatedRegistryCode}
              />
            </Box>
          ) : null;
        };

        return (
          <>
            <ReactTooltip
              id={row.original.id}
              backgroundColor="shadow"
              arrowColor="#D0D5DD"
              getContent={(dataTip) => tooltipContent(dataTip)}
              padding="0"
            />
            <Box sx={{ width: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <Text data-tip="rec-tooltip" data-for={row.original.id}>
                {[vintages, ...Object.values(rest)].flat().filter(Boolean).join(", ")}
              </Text>
            </Box>
          </>
        );
      },
      disableSortBy: true,
    },
    {
      Header: () => t("Order Type"),
      accessor: "position",
      disableSortBy: true,
      Cell: ({ value }) => {
        return (
          <Box
            sx={{
              variant: value === TradePosition.Ask ? "chips.error" : "chips.info",
              width: "40px",
            }}
          >
            {value}
          </Box>
        );
      },
    },
    {
      Header: t("Action"),
      id: "action",
      Cell: ({ row }: { row: Row<ForwardMarketTradeContract> }) => {
        const { position, accountId, registryCode, id } = row.original;
        const isOwnTrade = accountId === accountData.id;

        if (isOwnTrade) return null;

        const { canAcceptOrder, reason } = canAcceptForwardOrder({
          linkedRegistries: accountData.linkedRegistries,
          rowRegistryCode: registryCode,
          position,
        });

        if (canAcceptOrder) {
          return (
            <Button
              variant="pill.compact"
              sx={{ py: 1, minWidth: "110px" }}
              onClick={() => onAcceptDeal(row.original)}
              data-testid="forward-accept-order"
            >
              {t("Accept Order")}
            </Button>
          );
        }

        return reason.trim() ? <Tooltip content={<Text>{reason}</Text>} id={`accept-prevented-info-${id}`} /> : null;
      },
    },
  ];
  return tableColumns;
};
